import React, { useState, useEffect } from "react";
import { X, Download, Mail, ShieldCheck, Loader2 } from "lucide-react";
import { api } from "../lib/api";

const STORAGE_KEY = "nibro_pdf_lead_given_v1";
const BASE = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Minimal e-mail gate that wraps a PDF download.
 * - On first click: opens a modal, collects e-mail, posts to /api/leads/pdf, then triggers download.
 * - On subsequent downloads: skips the modal (localStorage flag).
 * - "Skip" link still allows direct download — keeps the open href crawlable for SEO.
 *
 * Usage:
 *   <PdfLeadGate kind="blog" slug={b.slug} title={b.title} pdfUrl={`${BASE}/api/blogs/${b.slug}/whitepaper.pdf`}>
 *     {(onClick) => <a onClick={onClick} ...>Download PDF</a>}
 *   </PdfLeadGate>
 */
export default function PdfLeadGate({ kind, slug, title, pdfUrl, children }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const alreadyCaptured = () => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  };

  const markCaptured = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      // noop
    }
  };

  const triggerDownload = () => {
    // Open in new tab — browser inherits Content-Disposition: attachment.
    window.open(pdfUrl, "_blank", "noopener,noreferrer");
  };

  const handleGateClick = (e) => {
    if (alreadyCaptured()) return; // Let the <a href> fire naturally.
    e.preventDefault();
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const clean = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(clean)) {
      setError("Vul een geldig e-mailadres in.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/leads/pdf", { email: clean, slug, kind, title });
      markCaptured();
      setOpen(false);
      setEmail("");
      triggerDownload();
    } catch (err) {
      setError("Er ging iets mis — probeer opnieuw of mail info@aramfix.com.");
    } finally {
      setBusy(false);
    }
  };

  const handleSkip = () => {
    markCaptured(); // user opted out, don't bug them again
    setOpen(false);
    triggerDownload();
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      {children(handleGateClick)}
      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-zinc-900/70 px-4 backdrop-blur-sm"
          data-testid="pdf-lead-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="pdf-lead-modal-close"
              className="absolute right-4 top-4 rounded-full p-1.5 text-zinc-500 transition hover:bg-zinc-100"
              aria-label="Sluiten"
            >
              <X size={18} />
            </button>

            <div
              className="border-b-2 border-aramfix-red/30 px-6 py-6"
              style={{ backgroundColor: "var(--nibro-sage)" }}
            >
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                <Download size={12} /> NIBRO Whitepaper
              </div>
              <h3 className="text-xl font-bold text-white sm:text-2xl">
                Eén klik en de PDF is van jou.
              </h3>
              <p className="mt-1 text-sm text-white/85">
                Laat je e-mail achter — we sturen geen spam, alleen relevante engineering content.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-6" data-testid="pdf-lead-form">
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-600">
                E-mailadres
              </label>
              <div className="mt-2 flex items-center gap-2 rounded-md border border-zinc-300 px-3 py-2.5 focus-within:border-nibro-sage-dark focus-within:ring-2 focus-within:ring-nibro-sage/30">
                <Mail size={16} className="text-zinc-400" />
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="naam@bedrijf.com"
                  className="flex-1 bg-transparent text-sm text-zinc-900 outline-none placeholder:text-zinc-400"
                  data-testid="pdf-lead-email-input"
                />
              </div>
              {error && (
                <p className="mt-2 text-xs text-aramfix-red" data-testid="pdf-lead-error">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={busy}
                data-testid="pdf-lead-submit"
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-md bg-aramfix-red px-5 py-3 text-sm font-bold uppercase tracking-wider text-white shadow-sm transition hover:bg-aramfix-red-hover disabled:opacity-60"
              >
                {busy ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Verzenden…
                  </>
                ) : (
                  <>
                    <Download size={14} /> Stuur me de PDF
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={handleSkip}
                data-testid="pdf-lead-skip"
                className="mt-3 block w-full text-center text-xs text-zinc-500 underline-offset-2 hover:text-zinc-700 hover:underline"
              >
                Sla over, alleen downloaden
              </button>

              <p className="mt-4 flex items-center justify-center gap-1.5 text-[11px] text-zinc-400">
                <ShieldCheck size={12} /> NIBRO BV · GDPR-conform · geen marketing-spam
              </p>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
